import type { IControlOption, IGroupedControlOption } from '@/Interfaces/ControlOption.interface';
import SearchSelect from '@/components/Select/SearchSelect';
import SelectionChip from '@/components/Select/SelectionChips/SelectionChip';
import type { GetOptionDisabled } from '@/types/Controls/TabSelect.types';
import { ArrowDropDown } from '@mui/icons-material';
import { Button, buttonClasses, popoverClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/system';
import { type Key, type ReactNode, forwardRef, useMemo } from 'react';

interface IProps {
  value: (IGroupedControlOption | IControlOption)[];
  options: (IGroupedControlOption | IControlOption)[];
  grouped?: boolean;
  onChange: (value: (IGroupedControlOption | IControlOption)[]) => void;
  getOptionDisabled?: GetOptionDisabled<IGroupedControlOption | IControlOption>;
  disabled?: boolean;
  startAdornment?: ReactNode;
  placeholder?: string;
  sx?: SxProps<Theme>;
  fullWidth?: boolean;
  limitTags?: number;
  label?: string;
  name: string;
  id: string;
  error?: boolean;
}

const StyledMainInput = styled(Button)(({ theme }) => ({
  height: 45,
  display: 'flex',
  color: '#84818A',
  gap: '6px',
  fontWeight: 400,
  '& svg': {
    color: '#9A9AAB',
  },
  [`&.${buttonClasses.colorError}`]: {
    borderColor: theme.palette.error.main,
    ':focus': {
      outline: `2px solid ${theme.palette.error.main}`,
    },
  },
  ':focus': {
    outline: `2px solid ${theme.palette.primary.main}`,
    border: '1px solid transparent',
  },
}));

const ColumnSelect = forwardRef<HTMLDivElement, IProps>(
  (
    {
      startAdornment,
      placeholder,
      options,
      value,
      onChange,
      getOptionDisabled,
      disabled,
      sx,
      fullWidth,
      grouped,
      limitTags = 3,
      label = 'Dimension',
      name,
      id,
      error = false,
    }: IProps,
    ref,
  ) => {
    const visibleTags = useMemo(() => value?.slice(0, limitTags) ?? [], [limitTags, value]);

    const menuWidth = document.getElementById(id)?.clientWidth || 0;

    return (
      <SearchSelect
        sx={{
          [`& .${popoverClasses.paper}`]: {
            width: menuWidth,
            minWidth: 300,
          },
        }}
        confirmLabel={`Add ${placeholder}`}
        grouped={grouped}
        label={label}
        onChange={onChange}
        getOptionDisabled={getOptionDisabled}
        options={options}
        value={value}
        renderInput={props => (
          <StyledMainInput
            id={id}
            sx={sx}
            disabled={disabled}
            variant='outlined'
            fullWidth={fullWidth}
            disableRipple
            name={name}
            color={error ? 'error' : 'primary'}
            //@ts-expect-error ref type
            ref={ref}
            {...props}
          >
            <div>{startAdornment}</div>
            <div className='flex justify-start flex-1 gap-[4px] overflow-hidden'>
              {value?.length ? (
                <>
                  {visibleTags.map(option => (
                    <SelectionChip key={option.value as Key} label={option.label} />
                  ))}
                  {limitTags < value.length ? <SelectionChip label={`+${value.length - limitTags}`} /> : null}
                </>
              ) : (
                placeholder
              )}
            </div>
            <ArrowDropDown />
          </StyledMainInput>
        )}
      />
    );
  },
);

export default ColumnSelect;
